import React, { useEffect, useState, useRef } from "react";
import "./productList.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Dropdown from "react-dropdown";
import { FaTimesCircle, FaSearch, FaChevronDown, FaEdit, FaEye, FaPrint, FaBox, FaTags, FaIndustry, FaLink, FaFilter } from "react-icons/fa";
import { IoBookOutline, IoWarningOutline, IoTriangleOutline } from "react-icons/io5";
import { MdDoNotDisturb } from "react-icons/md";
import { useCurrency } from '../currency/currencyContext';
import Swal from 'sweetalert2';

function ProductList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency();

  // Initialize states from URL parameters
  const [productOrder, setProductOrder] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || "");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || null);
  const [implantCompanyOptions, setImplantCompanyOptions] = useState([]);
  const [selectedImplantCompany, setSelectedImplantCompany] = useState(searchParams.get('company') || null);
  const [variationOptions, setVariationOptions] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(searchParams.get('variation') || null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || null);
  const [loading, setLoading] = useState(true);
  const [showNoProductsAlert, setShowNoProductsAlert] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOn, setIsOn] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductsList, setSelectedProductsList] = useState([]);
  const [bulkPrice, setBulkPrice] = useState("");
  const [editingProductId, setEditingProductId] = useState(null);
  const [updatingStock, setUpdatingStock] = useState(false);
  
  // NEW: State for category stock summary
  const [categoryStockSummary, setCategoryStockSummary] = useState({
    totalStock: 0,
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
    totalProducts: 0
  });

  const [selectedStockStatus, setSelectedStockStatus] = useState(searchParams.get('stockStatus') || null);
  const stockStatusOptions = [
    { value: null, label: "All Stock Status" },
    { value: "in_stock", label: "In Stock" },
    { value: "out_of_stock", label: "Out of Stock" },
    { value: "low_stock", label: "Low Stock" }
  ];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('perPage')) || 10);

  // Function to update URL parameters
  const updateURLParams = () => {
    const params = {};

    if (selectedCategory) params.category = selectedCategory;
    if (selectedBrand) params.brand = selectedBrand;
    if (selectedImplantCompany) params.company = selectedImplantCompany;
    if (selectedVariation) params.variation = selectedVariation;
    if (selectedType) params.type = selectedType;
    if (selectedStockStatus) params.stockStatus = selectedStockStatus;
    if (searchQuery) params.search = searchQuery;
    if (currentPage > 1) params.page = currentPage;
    if (itemsPerPage !== 10) params.perPage = itemsPerPage;

    setSearchParams(params);
  };

  // Update URL whenever filters change
  useEffect(() => {
    updateURLParams();
  }, [
    selectedCategory,
    selectedBrand,
    selectedImplantCompany,
    selectedVariation,
    selectedType,
    selectedStockStatus,
    searchQuery,
    currentPage,
    itemsPerPage
  ]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await fetch("https://backend.hira-tech.co.uk/product/getAllproduct/");
        const data = await res.json();
        setProductOrder(data);
        setProducts(data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/product/api/categories/");
        const data = await res.json();
        const options = [
          { value: null, label: "All Categories" },
          ...data.map((cat) => ({ value: cat.category_Name, label: cat.category_Name })),
        ];
        setCategoryOptions(options);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch brands (all)
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/product/api/brands/");
        const data = await res.json();
        setAllBrands(data);
      } catch (err) {
        console.error("Error fetching brands:", err);
      }
    };
    fetchBrands();
  }, []);

  // Filter brand options based on selected implant company
  useEffect(() => {
    if (selectedImplantCompany && allBrands.length > 0) {
      const filtered = allBrands.filter(
        (brand) => brand.implant_company?.company_name === selectedImplantCompany
      );
      const options = [
        { value: null, label: "All Brands" },
        ...filtered.map((brand) => ({
          value: brand.brand_name,
          label: brand.brand_name,
        })),
      ];
      setBrandOptions(options);
    } else {
      const options = [
        { value: null, label: "All Brands" },
        ...allBrands.map((brand) => ({
          value: brand.brand_name,
          label: brand.brand_name,
        })),
      ];
      setBrandOptions(options);
    }
  }, [selectedImplantCompany, allBrands]);

  // Implant Companies
  useEffect(() => {
    const fetchImplantCompanies = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/product/api/implant-companies/");
        const data = await res.json();
        const options = [
          { value: null, label: "All Companies" },
          ...data.map((comp) => ({ value: comp.company_name, label: comp.company_name })),
        ];
        setImplantCompanyOptions(options);
      } catch (err) {
        console.error("Error fetching implant companies:", err);
      }
    };
    fetchImplantCompanies();
  }, []);

  // Variations
  useEffect(() => {
    const fetchVariations = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/product/api/categories/");
        const data = await res.json();

        let matchedCategory = null;

        for (const category of data) {
          const hasMatchingType = category.types.some(
            (t) => t.type_name === selectedType
          );
          const isMatchingCategory =
            category.category_Name === selectedCategory;

          if (hasMatchingType && isMatchingCategory) {
            matchedCategory = category;
            break;
          }
        }

        const options = matchedCategory
          ? [
              { value: null, label: "All Variations" },
              ...matchedCategory.var.map((v) => ({
                value: v.variation_name,
                label: v.variation_name,
              })),
            ]
          : [{ value: null, label: "No Variations" }];

        setVariationOptions(options);
      } catch (err) {
        console.error("Error fetching variations by type/category:", err);
      }
    };

    if (selectedType && selectedCategory) {
      fetchVariations();
    } else {
      setVariationOptions([{ value: null, label: "All Variations" }]);
    }
  }, [selectedCategory, selectedType]);

  // Types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/product/api/type/");
        const data = await res.json();
        const options = [
          { value: null, label: "All Types" },
          ...data.map((t) => ({ value: t.type_name, label: t.type_name })),
        ];
        setTypeOptions(options);
      } catch (err) {
        console.error("Error fetching types:", err);
      }
    };
    fetchTypes();
  }, []);

  // NEW: Calculate category stock summary
  const calculateCategoryStockSummary = (filteredProducts) => {
    const summary = {
      totalStock: 0,
      inStock: 0,
      lowStock: 0,
      outOfStock: 0,
      totalProducts: filteredProducts.length
    };

    filteredProducts.forEach(product => {
      const stock = parseInt(product.product_Stock) || 0;
      summary.totalStock += stock;
      
      if (stock === 0) {
        summary.outOfStock++;
      } else if (stock <= 15) {
        summary.lowStock++;
      } else {
        summary.inStock++;
      }
    });

    setCategoryStockSummary(summary);
  };

  // Filtering logic with Stock Status filter
  useEffect(() => {
    let filtered = [...productOrder];

    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => p.product_category?.category_Name === selectedCategory
      );
    }

    if (selectedBrand) {
      filtered = filtered.filter(
        (p) => p.product_brand?.brand_name === selectedBrand
      );
    }

    if (selectedImplantCompany) {
      filtered = filtered.filter(
        (p) => p.implant_company?.company_name === selectedImplantCompany
      );
    }

    if (selectedVariation) {
      filtered = filtered.filter(
        (p) => p.product_variation?.variation_name === selectedVariation
      );
    }

    if (selectedType) {
      filtered = filtered.filter(
        (p) => p.type?.type_name === selectedType
      );
    }

    if (selectedStockStatus) {
      filtered = filtered.filter((p) => {
        const stock = parseInt(p.product_Stock) || 0;

        switch (selectedStockStatus) {
          case "in_stock":
            return stock > 15;
          case "out_of_stock":
            return stock === 0;
          case "low_stock":
            return stock > 0 && stock <= 15;
          default:
            return true;
        }
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim().replace(/-/g, '');
      filtered = filtered.filter((p) => {
        const productName = p.product_Name?.toLowerCase() || '';
        const sku = p.SKU?.toLowerCase().replace(/-/g, '') || '';
        return productName.includes(query) || sku.includes(query);
      });
    }

    // NEW: Calculate stock summary for filtered products (especially for selected category)
    calculateCategoryStockSummary(filtered);

    setProducts(filtered);
    setCurrentPage(1);
    setShowNoProductsAlert(filtered.length === 0 &&
      (selectedCategory || selectedBrand || selectedImplantCompany ||
       selectedVariation || selectedType || selectedStockStatus || searchQuery.trim()));
  }, [
    productOrder,
    selectedCategory,
    selectedBrand,
    selectedImplantCompany,
    selectedVariation,
    selectedType,
    selectedStockStatus,
    searchQuery,
  ]);

  const handleStockEdit = (product) => {
    setEditingProductId(product.product_Id);
  };

  const handleStockSave = async (productId, newStockValue) => {
    if (newStockValue === "" || isNaN(newStockValue) || newStockValue < 0) {
      alert("Please enter a valid stock number");
      setEditingProductId(null);
      return;
    }

    setUpdatingStock(true);
    try {
      const res = await fetch(
        `https://backend.hira-tech.co.uk/product/api/update-stock/${productId}/`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_Stock: parseInt(newStockValue) }),
        }
      );

      if (res.ok) {
        setProducts(prevProducts =>
          prevProducts.map(p =>
            p.product_Id === productId
              ? { ...p, product_Stock: parseInt(newStockValue) }
              : p
          )
        );
        setProductOrder(prev =>
          prev.map(p =>
            p.product_Id === productId
              ? { ...p, product_Stock: parseInt(newStockValue) }
              : p
          )
        );
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Stock updated successfully',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          background: '#d4edda',
          iconColor: '#28a745',
          customClass: {
            popup: 'animated fadeIn faster'
          }
        });
      } else {
        const error = await res.json();
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error.error || 'Failed to update stock',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
          background: '#f8d7da',
          iconColor: '#dc3545'
        });
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setEditingProductId(null);
      setUpdatingStock(false);
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = products.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(products.length / itemsPerPage);

  // Generate pagination numbers with ellipsis
  const generatePaginationNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        endPage = 4;
      }

      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handleEdit = (productId) => {
    navigate(`/admin/product/edit/${productId}`);
  };

  const handleNew = () => {
    navigate(`/admin/product/new`);
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedImplantCompany(null);
    setSelectedVariation(null);
    setSelectedType(null);
    setSelectedStockStatus(null);
    setSearchQuery("");
    setCurrentPage(1);
    setItemsPerPage(10);
    setShowNoProductsAlert(false);
    setSearchParams({});
  };

  const handleBulkPriceUpdate = async () => {
    if (!bulkPrice || selectedProductsList.length === 0) return;

    const res = await fetch("https://backend.hira-tech.co.uk/product/api/bulk-update-price/", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        product_ids: selectedProductsList,
        new_price: bulkPrice,
      }),
    });

    const data = await res.json();

    if (res.ok) {
      alert("Prices updated successfully!");
      window.location.reload();
    } else {
      alert("Error updating prices");
    }
  };

  const printLabel = (country) => {
    const labelElement = document.getElementById("print-label");
    const ukAddress = `02 Ulverscroft Road,<br /> Coventry, United Kingdom, CV3 5EZ`;
    const pkAddress = `House # 13, Street # 14, Sector C1 -<br /> Bahria Enclave, Islamabad`;

    const labelClone = labelElement.cloneNode(true);
    const addressElements = labelClone.getElementsByClassName("label-address");
    const madeInElements = labelClone.getElementsByClassName("made-in-text");

    for (let el of addressElements) {
      el.innerHTML = country === 'uk' ? ukAddress : pkAddress;
    }

    for (let el of madeInElements) {
      el.textContent = `Made in ${country === 'uk' ? 'UK' : 'PK'}`;
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Label</title>
          <style>
            @page { size: 60mm 70mm; margin: 0; }
            body { display: flex; justify-content: center; align-items: center; height: 98vh; margin: 0; background-color: white; }
            #print-label { width: 60mm; height: 70mm; text-align: center; padding: 10px; }
          </style>
        </head>
        <body>
          <div id="print-label">${labelClone.innerHTML}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  if (loading) return <div className="admin-products-loading">Loading....</div>;

  return (
    <div className="admin-products-page">
      <div className="admin-products-header">
        <h1 className="admin-products-title">Products Management</h1>
      </div>

      {/* Search and Filters Section */}
      <div className="admin-products-controls">
        <div className="admin-products-search-container">
          <div className="admin-products-search-bar">
            <FaSearch className="admin-products-search-icon" />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="admin-products-filters-horizontal">
          <div className="filter-section">
            <FaFilter className="filter-icon" />
            <span className="filter-label">Filters:</span>
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={typeOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedType(opt.value)}
              value={typeOptions.find((opt) => opt.value === selectedType) || typeOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={categoryOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedCategory(opt.value)}
              value={categoryOptions.find((opt) => opt.value === selectedCategory) || categoryOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={variationOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedVariation(opt.value)}
              value={variationOptions.find((opt) => opt.value === selectedVariation) || variationOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={implantCompanyOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedImplantCompany(opt.value)}
              value={implantCompanyOptions.find((opt) => opt.value === selectedImplantCompany) || implantCompanyOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={brandOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedBrand(opt.value)}
              value={brandOptions.find((opt) => opt.value === selectedBrand) || brandOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>

          <div className="admin-products-filter-group">
            <Dropdown
              options={stockStatusOptions}
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              onChange={(opt) => setSelectedStockStatus(opt.value)}
              value={stockStatusOptions.find((opt) => opt.value === selectedStockStatus) || stockStatusOptions[0]}
              arrowClosed={<FaChevronDown className="admin-products-dropdown-arrow-icon" />}
              arrowOpen={<FaChevronDown className="admin-products-dropdown-arrow-icon open" />}
            />
          </div>
        </div>

        <button
          className="admin-products-clear-filters"
          onClick={handleClearFilters}
          style={{
            width: "120px",
            marginLeft: "0",
            padding: "5px 10px"
          }}
        >
          <FaTimesCircle /> Clear All
        </button>
      </div>

      {/* NEW: Category Stock Summary Section */}
      {selectedCategory && (
        <div className="category-stock-summary">
          <div className="summary-header">
            <h3>
              <FaBox className="summary-icon" />
              Category Stock Summary: <span className="category-name">{selectedCategory}</span>
            </h3>
          </div>
          <div className="summary-cards">
            <div className="summary-card total-stock">
              <div className="card-icon">
                <FaBox />
              </div>
              <div className="card-details">
                <span className="card-label">Total Stock</span>
                <span className="card-value">{categoryStockSummary.totalStock}</span>
              </div>
            </div>
            
            <div className="summary-card total-products">
              <div className="card-icon">
                <FaTags />
              </div>
              <div className="card-details">
                <span className="card-label">Total Products</span>
                <span className="card-value">{categoryStockSummary.totalProducts}</span>
              </div>
            </div>
            
            <div className="summary-card in-stock-card">
              <div className="card-icon">
                <IoBookOutline />
              </div>
              <div className="card-details">
                <span className="card-label">In Stock</span>
                <span className="card-value">{categoryStockSummary.inStock}</span>
              </div>
            </div>
            
            <div className="summary-card low-stock-card">
              <div className="card-icon">
                <IoWarningOutline />
              </div>
              <div className="card-details">
                <span className="card-label">Low Stock</span>
                <span className="card-value">{categoryStockSummary.lowStock}</span>
              </div>
            </div>
            
            <div className="summary-card out-of-stock-card">
              <div className="card-icon">
                <MdDoNotDisturb />
              </div>
              <div className="card-details">
                <span className="card-label">Out of Stock</span>
                <span className="card-value">{categoryStockSummary.outOfStock}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Products Found Alert */}
      {showNoProductsAlert && (
        <div className="admin-products-alert">
          No products found matching your filters. Please try different criteria.
        </div>
      )}

      {/* Products Table */}
      <div className="admin-products-table-container">
        <div className="table-header">
          <div className="table-results">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, products.length)} of {products.length} products
          </div>
          <div className="table-controls">
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="items-per-page-select"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>

        <div className="bulk-update-box">
          <input
            type="number"
            placeholder="Enter new price"
            value={bulkPrice}
            onChange={(e) => setBulkPrice(e.target.value)}
            style={{ border: "2px solid #000", borderRadius: "4px", padding: "5px" }}
          />
          <button
            disabled={selectedProductsList.length === 0 || bulkPrice === ""}
            onClick={handleBulkPriceUpdate}
          >
            Update Selected Prices
          </button>
        </div>

        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th className="select-column">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProductsList(products.map((p) => p.product_Id));
                      } else {
                        setSelectedProductsList([]);
                      }
                    }}
                    checked={
                      products.length > 0 &&
                      selectedProductsList.length === products.length
                    }
                  />
                </th>
                <th className="image-column">Image</th>
                <th className="name-column">Product Name</th>
                <th className="sku-column">SKU</th>
                <th className="category-column">Category</th>
                <th className="brand-column">Brand</th>
                <th className="price-column">Price</th>
                <th className="stock-column">Stock</th>
                <th className="status-column">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((product, index) => (
                <tr
                  key={index}
                  className={`product-row 
                    ${selectedProductsList.includes(product.product_Id) ? 'selected' : ''}
                    ${editingProductId === product.product_Id ? 'editing' : ''}
                  `}
                >
                  <td className="select-cell">
                    <input
                      type="checkbox"
                      checked={selectedProductsList.includes(product.product_Id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProductsList([...selectedProductsList, product.product_Id]);
                        } else {
                          setSelectedProductsList(
                            selectedProductsList.filter((id) => id !== product.product_Id)
                          );
                        }
                      }}
                    />
                  </td>
                  <td className="image-cell">
                    <div className="product-image">
                      <img
                        src={`https://backend.hira-tech.co.uk${product.imageGallery?.image1}`}
                        alt={product.product_Name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/50x50?text=No+Image";
                        }}
                      />
                    </div>
                  </td>
                  <td className="name-cell">
                    <div className="product-name">{product.product_Name}</div>
                    <div className="product-description">
                      {product.product_Description?.substring(0, 60)}...
                    </div>
                  </td>
                  <td className="sku-cell">
                    <code>{product.SKU}</code>
                  </td>
                  <td className="pricee-cell">
                    {product.product_category?.category_Name || 'N/A'}
                  </td>
                  <td className="pricee-cell">
                    {product.product_brand?.brand_name || 'N/A'}
                  </td>
                  <td className="price-cell">
                    {formatPrice(product.product_Price)}
                  </td>
                  <td className="stock-cell">
                    {product.product_Stock > 0 ? (
                      <span
                        className="editable-stock"
                        onClick={() => handleStockEdit(product)}
                        style={{
                          cursor: "pointer",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: "#f0f8ff",
                          display: "inline-block",
                          minWidth: "60px",
                          textAlign: "center",
                          fontWeight: "500",
                        }}
                      >
                        {editingProductId === product.product_Id ? (
                          <input
                            type="number"
                            min="0"
                            autoFocus
                            defaultValue={product.product_Stock}
                            onBlur={(e) => handleStockSave(product.product_Id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleStockSave(product.product_Id, e.target.value);
                              }
                              if (e.key === "Escape") {
                                setEditingProductId(null);
                              }
                            }}
                            style={{
                              width: "60px",
                              padding: "4px",
                              border: "2px solid #007bff",
                              borderRadius: "4px",
                              textAlign: "center",
                            }}
                          />
                        ) : (
                          product.product_Stock
                        )}
                      </span>
                    ) : (
                      <span className="out-of-stock">Out of Stock</span>
                    )}
                  </td>
                  <td className="status-cell">
                    <span className={`status-badge ${
                      product.product_Stock === 0 
                        ? 'out-of-stock' 
                        : product.product_Stock <= 15 
                          ? 'low-stock' 
                          : 'in-stock'
                    }`}>
                      {product.product_Stock === 0
                        ? 'Out of Stock'
                        : product.product_Stock <= 15
                          ? 'Low Stock'
                          : 'In Stock'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination">
              <button
                className="pagination-btn pagination-prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {generatePaginationNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}

              <button
                className="pagination-btn pagination-next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductList;